import { AsyncPipe } from '@angular/common';
import {
    Component,
    effect,
    EventEmitter,
    inject,
    input,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    getAddressComponent,
    getCityComponent,
    GoogleMapsAutocompleteDirective,
} from '@app/directives/google-maps-autocomplete/google-maps-autocomplete.directive';
import { LoadingCardComponent } from '@app/v2/components/loading-card/loading-card.component';
import {
    MdsAutocompleteComponent,
    MdsDropdownComponent,
    MdsOption,
    MdsTextFieldComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { CountryViewModel } from '@models/country';
import { CountryStateService } from '@services/state-management/country-state.service';
import { RegionStateService } from '@services/state-management/region-state.service';
import { SiteStateService } from '@services/state-management/site-state.service';
import { maxLengthValidator, notWhitespaceValidator, zipCodeFormatValidator } from '@utility/utility.validators';
import { Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { InputChange } from 'src/app/models/event-objects/input-change';
import { Address, Site } from 'src/app/models/site';

@Component({
    selector: 'medpace-site-address',
    standalone: true,
    templateUrl: './site-address.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MdsTextFieldComponent,
        AsyncPipe,
        MdsAutocompleteComponent,
        MdsDropdownComponent,
        LoadingCardComponent,
        GoogleMapsAutocompleteDirective,
    ],
})
export class MedpaceSiteAddressComponent implements OnInit, OnDestroy {
    private stateService = inject(SiteStateService);
    private countryStateService = inject(CountryStateService);
    private regionStateService = inject(RegionStateService);

    content: Address;
    site$: Observable<Site> = this.stateService.getSite();
    site = toSignal(this.site$);
    private componentDestroyed$: Subject<boolean> = new Subject();

    isModal = input<boolean>(false);
    isEditing = input<boolean>(false);

    @Input() parentFormGroup: FormGroup;

    addressFormGroup = new FormGroup({
        line1Control: new FormControl<string>('', [
            Validators.required,
            notWhitespaceValidator(),
            maxLengthValidator(150),
        ]),
        line2Control: new FormControl<string>('', [notWhitespaceValidator(), maxLengthValidator(100)]),
        cityControl: new FormControl<string>('', [
            Validators.required,
            notWhitespaceValidator(),
            maxLengthValidator(100),
        ]),
        stateOrProvinceControl: new FormControl<string>('', [notWhitespaceValidator(), maxLengthValidator(25)]),
        postalCodeControl: new FormControl<string>('', [notWhitespaceValidator(), zipCodeFormatValidator]),
        regionControl: new FormControl<string>('', [
            Validators.required,
            notWhitespaceValidator(),
            maxLengthValidator(75),
        ]),
        countryControl: new FormControl<string>('', [
            Validators.required,
            notWhitespaceValidator(),
            maxLengthValidator(65),
        ]),
    });

    countries$: Observable<string[]> = this.countryStateService.getCountriesStringArray();

    emitCountryValue$: Observable<string>;

    @Output() inputChangeEvent = new EventEmitter<InputChange>();

    regions: any[] = [];
    filteredCountries: Observable<MdsOption[]>;
    loaderSpinner: boolean = true;
    regionStateType$ = of('Province / Region / State');

    disableCountryControl = effect(() => {
        const isEditing = this.isEditing();
        const payQuicker = this.site()?.payment?.payQuicker;
        const countryControl = this.addressFormGroup.get('countryControl');

        if (!countryControl) return;

        if (isEditing && payQuicker) {
            countryControl.disable();
        } else {
            countryControl.enable();
        }
    });

    ngOnInit(): void {
        this.initializeFormGroup();

        this.site$
            .pipe(
                takeUntil(this.componentDestroyed$),
                switchMap((resultSite) =>
                    this.regions$.pipe(
                        map((x) => {
                            this.regions = x;
                            return resultSite;
                        })
                    )
                )
            )
            .subscribe((result) => {
                if (result?.address) {
                    this.content = result.address;
                    this.setValues();
                    this.loaderSpinner = false;
                }
            });

        this.addressFormGroup
            .get('countryControl')
            ?.valueChanges.pipe(
                filter(Boolean),
                takeUntil(this.componentDestroyed$),
                tap((country) => {
                    this.regionStateType$ = this.countryStateService.getRegionStateType(country);
                }),
                switchMap((countryString) => this.countryStateService.getCountryByName(countryString)),
                filter(Boolean),
                tap((countryModel: CountryViewModel) => {
                    //country is valid, emit value
                    this.emitChange(countryModel.viewValue, { id: 'country' });
                    this.stateService.setSiteCountry(countryModel.viewValue);
                })
            )
            .subscribe();
    }

    googleMapsAutocompletePlaceChange(place: google.maps.places.Place) {
        const addressComponents = place.addressComponents ?? [];
        const line1 = `${getAddressComponent(
            'street_number',
            addressComponents
        )} ${getAddressComponent('route', addressComponents)}`;
        const city = getCityComponent(place);
        const state = getAddressComponent('administrative_area_level_1', addressComponents);
        const postalCode = getAddressComponent('postal_code', addressComponents);
        const country = getAddressComponent('country', addressComponents);

        this.addressFormGroup.controls.line1Control.setValue(line1);
        this.addressFormGroup.controls.cityControl.setValue(city);
        this.addressFormGroup.controls.stateOrProvinceControl.setValue(state);
        this.addressFormGroup.controls.postalCodeControl.setValue(postalCode);

        this.inputChangeEvent.emit({ target: 'line1', value: line1 });
        this.inputChangeEvent.emit({ target: 'city', value: city });
        this.inputChangeEvent.emit({ target: 'stateOrProvince', value: state });
        this.inputChangeEvent.emit({ target: 'postalCode', value: postalCode });

        if (!this.site()?.payment?.payQuicker) {
            this.addressFormGroup.controls.countryControl.setValue(country);
            this.inputChangeEvent.emit({ target: 'country', value: country });
        }
    }

    ngOnDestroy() {
        this.componentDestroyed$.next(true);
        this.componentDestroyed$.complete();
    }

    regions$ = this.regionStateService
        .getRegions()
        .pipe(map((result) => result.map((x) => ({ value: x.name, viewValue: x.name }))));

    initializeFormGroup() {
        this.parentFormGroup.addControl('addressFormGroup', this.addressFormGroup);
    }

    setValues() {
        if (this.content) {
            this.addressFormGroup.controls.line1Control.setValue(this.content.line1);
            this.addressFormGroup.controls.line2Control.setValue(this.content.line2);
            this.addressFormGroup.controls.cityControl.setValue(this.content.city);
            this.addressFormGroup.controls.stateOrProvinceControl.setValue(this.content.stateOrProvince);
            this.addressFormGroup.controls.postalCodeControl.setValue(this.content.postalCode);
            this.addressFormGroup.controls.regionControl.setValue(this.content.region);
            this.addressFormGroup.controls.countryControl.setValue(this.content.country);
        }
    }

    emitChange: (value: any, target: any) => void = (value: any, target: any) => {
        this.inputChangeEvent.emit({ target: target.id, value: value });
    };
    emitStringChange(event: Event, target: any) {
        const input = event.target as HTMLInputElement;
        const value = input.value;
        this.inputChangeEvent.emit({ target: target.id, value: value });
    }

    selected(event: any): void {
        this.inputChangeEvent.emit({ target: 'country', value: event.option.value });
    }
}
